/*
 * Mobile pinch zoom + pan for the product media gallery.
 *
 * Smoothness techniques:
 *  - Continuous rAF loop with lerp smoothing: the image eases toward the target
 *    scale/position every frame instead of jumping on each touchmove (removes
 *    finger jitter, feels native).
 *  - Elastic limits: you can pinch slightly past min/max and past the pan edges;
 *    on release it springs back (iOS-style rubber-banding).
 *  - GPU layer promoted on first touch (will-change) and kept while zoomed.
 *  - Hi-res source swap on first zoom so the image stays crisp at 3-4x.
 *
 * Native one-finger gallery swiping still works at rest (touch-action pan-x pan-y);
 * we only own the gestures while pinching or zoomed. Touch devices only.
 *
 * Two things used to make this feel laggy on a phone:
 *  1. touchmove was bound non-passive for the life of the page, so EVERY touch
 *     over a product photo — plain vertical scrolling included — had to wait on
 *     JavaScript before the browser could move anything. It is now bound only
 *     for the duration of a real pinch/pan and removed again on release.
 *  2. The hi-res source was fetched and swapped in mid-gesture, so the first
 *     pinch paid for decoding a ~1800px JPEG while the fingers were moving.
 *     It is now prepared on first touch, decoded off the main thread, and
 *     swapped in only while no fingers are down.
 */
(function () {
  if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) return;

  var MAX = 4;          // max settled zoom
  var OVER = 1.25;      // elastic overshoot factor beyond MAX (and below 1)
  var TAU_SETTLE = 55;  // smoothing time-constant (ms) for the spring-back after release

  /* No smoothing while the fingers are down. The image used to ease toward the
     touch target, which at 60Hz closed only ~43% of the gap per frame — about
     70ms of visible trailing behind the pinch. That reads as "slow" even when
     every frame is on time. Native pinch is 1:1, so this is too; the easing is
     kept only for the spring-back after release. */

  function setup(media) {
    if (media.__mdmZoom) return;
    var img = media.querySelector('img');
    if (!img) return;
    media.__mdmZoom = true;

    // current (rendered) state
    var scale = 1, tx = 0, ty = 0;
    // target state (follows fingers; render lerps toward it)
    var tScale = 1, tTx = 0, tTy = 0;
    // cached box, measured once per gesture
    var bw = 0, bh = 0, bl = 0, bt = 0;
    var startDist = 0, startScale = 1, focalIx = 0, focalIy = 0, focalX = 0, focalY = 0;
    var startX = 0, startY = 0, startTx = 0, startTy = 0;
    var mode = null;          // 'pinch' | 'pan'
    var touching = false;
    var rafId = 0;
    var zoomed = false;
    var hiresDone = false;

    media.style.overflow = 'hidden';
    media.style.contain = 'layout paint';
    img.style.transformOrigin = '0 0';
    img.style.backfaceVisibility = 'hidden';
    img.style.touchAction = 'pan-x pan-y';

    function measure() { var b = media.getBoundingClientRect(); bw = b.width; bh = b.height; bl = b.left; bt = b.top; }

    function setZoomed(z) {
      if (z === zoomed) return;
      zoomed = z;
      img.style.touchAction = z ? 'none' : 'pan-x pan-y';
      if (z) img.style.willChange = 'transform';
    }

    /* Higher-resolution source so the image stays sharp at 3-4x.
       Prepared on first touch and decoded off the main thread; the swap itself
       only happens while no fingers are down, so a gesture never pays for it. */
    var hiresUrl = null, hiresReady = false, hiresSwapped = false;

    function prepareHires() {
      if (hiresDone) return;
      hiresDone = true;
      var src = img.currentSrc || img.src;
      if (!src || !/width=\d+/.test(src)) return;

      /* Ask for what this screen can actually use rather than a flat 1800. */
      var need = Math.round(media.getBoundingClientRect().width * (window.devicePixelRatio || 1) * 2);
      var want = Math.max(900, Math.min(1800, need));
      var big = src.replace(/width=\d+/, 'width=' + want);
      if (big === src) return;

      var pre = new Image();
      pre.src = big;
      var ready = function () { hiresUrl = big; hiresReady = true; swapHires(); };
      if (pre.decode) pre.decode().then(ready).catch(function () {});
      else pre.onload = ready;
    }

    function swapHires() {
      if (!hiresReady || hiresSwapped || touching) return;
      hiresSwapped = true;
      img.srcset = '';
      img.sizes = '';
      img.src = hiresUrl;
    }

    // clamp target pan to (elastic) bounds
    function boundsFor(s) {
      return { minX: bw * (1 - s), minY: bh * (1 - s) };
    }
    function rubber(v, min, max, give) {
      if (v > max) return max + (v - max) * give;
      if (v < min) return min + (v - min) * give;
      return v;
    }

    function render() {
      img.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + scale + ')';
    }

    var lastT = 0;
    function tick(now) {
      rafId = 0;
      if (!now) now = performance.now();
      var dt = lastT ? Math.min(50, now - lastT) : 16.7;
      lastT = now;
      // exponential smoothing, refresh-rate independent — release only
      var k = 1 - Math.exp(-dt / TAU_SETTLE);

      // when idle (not touching), spring the target back inside hard limits
      if (!touching) {
        var settled = Math.min(MAX, Math.max(1, tScale));
        if (settled !== tScale) {
          // re-anchor pan so the spring zoom keeps the same center feel
          var cx = bw / 2, cy = bh / 2;
          var ratio = settled / tScale;
          tTx = cx - (cx - tTx) * ratio;
          tTy = cy - (cy - tTy) * ratio;
          tScale = settled;
        }
        if (tScale <= 1.001) { tScale = 1; tTx = 0; tTy = 0; }
        var bnd = boundsFor(tScale);
        tTx = Math.min(0, Math.max(bnd.minX, tTx));
        tTy = Math.min(0, Math.max(bnd.minY, tTy));
      }

      if (touching) {
        // follow the fingers exactly
        scale = tScale; tx = tTx; ty = tTy;
      } else {
        scale += (tScale - scale) * k;
        tx += (tTx - tx) * k;
        ty += (tTy - ty) * k;
      }

      var done = Math.abs(tScale - scale) < 0.002 && Math.abs(tTx - tx) < 0.25 && Math.abs(tTy - ty) < 0.25;
      if (done) { scale = tScale; tx = tTx; ty = tTy; }
      render();
      setZoomed(scale > 1.01 || tScale > 1.01);
      if (scale <= 1.001 && !touching && done) {
        img.style.willChange = '';
        img.style.transition = '';   // hand the hover effect back
      }

      if (!done || touching) rafId = requestAnimationFrame(tick);
      else lastT = 0;
    }
    function loop() { if (!rafId) rafId = requestAnimationFrame(tick); }

    function dist(t) { return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY); }

    /* Non-passive touchmove blocks the compositor until JS has run, so it is
       attached only while a gesture is actually in progress. */
    var moveBound = false;
    function bindMove() {
      if (moveBound) return;
      media.addEventListener('touchmove', onMove, { passive: false });
      moveBound = true;
    }
    function unbindMove() {
      if (!moveBound) return;
      media.removeEventListener('touchmove', onMove, { passive: false });
      moveBound = false;
    }

    media.addEventListener('touchstart', function (e) {
      prepareHires();
      // Promote before the gesture rather than on its first frame.
      img.style.willChange = 'transform';
      /* base.css animates transform on .product__media-item img over 0.4s for
         the desktop hover effect. Left in place it animates every frame this
         script writes, so the image permanently chases a 400ms-old target —
         which is what made zooming feel slow no matter how cheap the frames
         were. Suppressed for as long as the gesture owns the image, and put
         back when it settles at rest. */
      img.style.transition = 'none';
      if (e.touches.length === 2) {
        e.preventDefault();
        touching = true;
        img.style.willChange = 'transform';
        measure();
        mode = 'pinch';
        startDist = dist(e.touches) || 1;
        startScale = tScale;
        focalX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - bl;
        focalY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - bt;
        focalIx = (focalX - tTx) / tScale;
        focalIy = (focalY - tTy) / tScale;
        bindMove();
        loop();
      } else if (e.touches.length === 1 && (zoomed || tScale > 1.01)) {
        touching = true;
        measure();
        mode = 'pan';
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        startTx = tTx; startTy = tTy;
        bindMove();
        loop();
      } else {
        mode = null; // at rest — native gallery swipe
      }
    }, { passive: false });

    function onMove(e) {
      if (mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault(); e.stopPropagation();
        var raw = startScale * (dist(e.touches) / startDist);
        // elastic beyond [1, MAX]
        if (raw > MAX) raw = MAX + (raw - MAX) * 0.35;
        if (raw < 1) raw = 1 - (1 - raw) * 0.45;
        raw = Math.min(MAX * OVER, Math.max(1 / OVER, raw));
        tScale = raw;
        var bnd = boundsFor(tScale);
        tTx = rubber(focalX - focalIx * tScale, bnd.minX, 0, 0.3);
        tTy = rubber(focalY - focalIy * tScale, bnd.minY, 0, 0.3);
        loop();
      } else if (mode === 'pan' && e.touches.length === 1 && tScale > 1.01) {
        e.preventDefault(); e.stopPropagation();
        var bnd2 = boundsFor(tScale);
        tTx = rubber(startTx + (e.touches[0].clientX - startX), bnd2.minX, 0, 0.3);
        tTy = rubber(startTy + (e.touches[0].clientY - startY), bnd2.minY, 0, 0.3);
        loop();
      }
    }

    function end(e) {
      if (mode === 'pinch' && e.touches.length === 1 && tScale > 1.01) {
        // one finger lifted after pinch → continue panning with the other
        mode = 'pan';
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        startTx = tTx; startTy = tTy;
        return;
      }
      if (e.touches.length === 0) {
        touching = false;
        mode = null;
        unbindMove();
        if (tScale < 1.15) { tScale = 1; tTx = 0; tTy = 0; } // near-1 snaps fully out
        swapHires();
        loop(); // spring-back / settle
      }
    }
    media.addEventListener('touchend', end);
    media.addEventListener('touchcancel', end);
  }

  function init() {
    document.querySelectorAll('.product__media-list .product__media-item .media').forEach(setup);
  }

  if (document.readyState !== 'loading') init();
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
